import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ActivationFunnelService } from "@/services/analytics/ActivationFunnelService";
import { TwilioService } from "@/services/communication/TwilioService";
import { Agent } from '@/types/agent/agent';

interface PhoneNumber {
  id: string;
  phoneNumber: string;
  friendlyName: string;
  status: string;
  dateCreated: string;
}

interface ProvisionPhoneNumberParams {
  phoneNumber: string;
  friendlyName: string;
  agentId: string;
  countryCode: string;
  isTestMode?: boolean;
}

interface ProvisionTransferNumberParams {
  phoneNumber: string;
  friendlyName: string;
  description: string;
  agentId: string;
}

interface UpdatePhoneDestinationParams {
  phoneNumberId: string;
  friendlyName?: string;
  description?: string;
}

interface ProvisionEscalationNumberParams {
  phoneNumber: string;
  friendlyName: string;
  description: string;
  agentId: string;
}

interface RequestExistingNumberAttachmentParams {
  phoneNumber: string;
  friendlyName: string;
  agentId: string;
  source: "ported" | "own_twilio";
  countryCode?: string;
  businessName?: string;
  authorizedRepresentative?: string;
  contactEmail?: string;
  accountNumber?: string;
  currentCarrier?: string;
  billingAddress?: string;
  authorizationDocumentUrl?: string;
  latestBillUrl?: string;
  notes?: string;
}

interface CompleteExistingNumberAttachmentParams {
  phoneNumberRecordId?: number;
  phoneNumberId?: string;
  twilioIncomingPhoneNumberSid?: string;
}

interface MarkExistingNumberPortPendingParams {
  phoneNumberRecordId?: number;
  phoneNumberId?: string;
}

export const usePhoneNumber = (agent?: Agent) => {
  const queryClient = useQueryClient();

  const provisionMutation = useMutation({
    mutationFn: async (params: ProvisionPhoneNumberParams) => {
      const result = await TwilioService.provisionPhoneNumber(params);
      return {
        id: result.id,
        phoneNumber: result.phoneNumber,
        friendlyName: result.friendlyName,
        status: result.status,
        dateCreated: result.dateCreated,
      } as PhoneNumber;
    },
    onSuccess: () => {
      if (agent?.id) {
        // Activation funnel: a number is live, so the phone channel can now
        // reach this Genie. Fire and forget — never blocks provisioning.
        ActivationFunnelService.channelConnected("phone", "provisioned", agent.id);

        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const releaseMutation = useMutation({
    mutationFn: async ({ phoneNumberId, isTestMode }: { phoneNumberId: string; isTestMode?: boolean }) => {
      await TwilioService.releasePhoneNumber(phoneNumberId, isTestMode || false);
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const provisionTransferMutation = useMutation({
    mutationFn: async (params: ProvisionTransferNumberParams) => {
      const result = await TwilioService.provisionTransferNumber(params);
      return {
        id: result.id,
        phoneNumber: result.phoneNumber,
        friendlyName: result.friendlyName,
        status: result.status,
        dateCreated: result.dateCreated,
      } as PhoneNumber;
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const updateTransferMutation = useMutation({
    mutationFn: (params: UpdatePhoneDestinationParams) => TwilioService.updateTransferNumber(params),
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const provisionEscalationMutation = useMutation({
    mutationFn: async (params: ProvisionEscalationNumberParams) => {
      const result = await TwilioService.provisionEscalationNumber(params);
      return {
        id: result.id,
        phoneNumber: result.phoneNumber,
        friendlyName: result.friendlyName,
        status: result.status,
        dateCreated: result.dateCreated,
      } as PhoneNumber;
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const updateEscalationMutation = useMutation({
    mutationFn: (params: UpdatePhoneDestinationParams & { targetNumber?: string }) =>
      TwilioService.updateEscalationNumber(params),
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const releaseEscalationMutation = useMutation({
    mutationFn: async (phoneNumberId: string) => {
      await TwilioService.releaseEscalationNumber(phoneNumberId);
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const requestExistingNumberAttachmentMutation = useMutation({
    mutationFn: async (params: RequestExistingNumberAttachmentParams) => {
      const result = await TwilioService.requestExistingNumberAttachment(params);
      return {
        id: result.id,
        phoneNumber: result.phoneNumber,
        friendlyName: result.friendlyName,
        status: result.status,
        dateCreated: result.dateCreated,
      } as PhoneNumber;
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const completeExistingNumberAttachmentMutation = useMutation({
    mutationFn: async (params: CompleteExistingNumberAttachmentParams) => {
      const result = await TwilioService.completeExistingNumberAttachment(params);
      return {
        id: result.id,
        phoneNumber: result.phoneNumber,
        friendlyName: result.friendlyName,
        status: result.status,
        dateCreated: result.dateCreated,
      } as PhoneNumber;
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  const markExistingNumberPortPendingMutation = useMutation({
    mutationFn: async (params: MarkExistingNumberPortPendingParams) => {
      const result = await TwilioService.markExistingNumberPortPending(params);
      return {
        id: result.id,
        phoneNumber: result.phoneNumber,
        friendlyName: result.friendlyName,
        status: result.status,
        dateCreated: result.dateCreated,
      } as PhoneNumber;
    },
    onSuccess: () => {
      if (agent?.id) {
        queryClient.invalidateQueries({ queryKey: ["phoneNumber", agent.id] });
        queryClient.invalidateQueries({ queryKey: ["agent"] });
        queryClient.invalidateQueries({ queryKey: ["agents"] });
        queryClient.invalidateQueries({ queryKey: ["user-agents"] });
        queryClient.invalidateQueries({ queryKey: ["all-agents", agent.id] });
      }
    },
  });

  return {
    provision: provisionMutation.mutateAsync,
    release: (phoneNumberId: string, isTestMode?: boolean) => releaseMutation.mutateAsync({ phoneNumberId, isTestMode }),
    provisionTransfer: provisionTransferMutation.mutateAsync,
    updateTransfer: updateTransferMutation.mutateAsync,
    provisionEscalation: provisionEscalationMutation.mutateAsync,
    updateEscalation: updateEscalationMutation.mutateAsync,
    releaseEscalation: releaseEscalationMutation.mutateAsync,
    requestExistingNumberAttachment: requestExistingNumberAttachmentMutation.mutateAsync,
    completeExistingNumberAttachment: completeExistingNumberAttachmentMutation.mutateAsync,
    markExistingNumberPortPending: markExistingNumberPortPendingMutation.mutateAsync,
    isProvisioning: provisionMutation.isPending,
    isReleasing: releaseMutation.isPending,
    isProvisioningTransfer: provisionTransferMutation.isPending,
    isUpdatingTransfer: updateTransferMutation.isPending,
    isProvisioningEscalation: provisionEscalationMutation.isPending,
    isUpdatingEscalation: updateEscalationMutation.isPending,
    isReleasingEscalation: releaseEscalationMutation.isPending,
    isRequestingExistingNumberAttachment: requestExistingNumberAttachmentMutation.isPending,
    isCompletingExistingNumberAttachment: completeExistingNumberAttachmentMutation.isPending,
    isMarkingExistingNumberPortPending: markExistingNumberPortPendingMutation.isPending,
    provisionError: provisionMutation.error,
    releaseError: releaseMutation.error,
    provisionTransferError: provisionTransferMutation.error,
    provisionEscalationError: provisionEscalationMutation.error,
    releaseEscalationError: releaseEscalationMutation.error,
    requestExistingNumberAttachmentError: requestExistingNumberAttachmentMutation.error,
    completeExistingNumberAttachmentError: completeExistingNumberAttachmentMutation.error,
    markExistingNumberPortPendingError: markExistingNumberPortPendingMutation.error,
  };
};
